import { apiClient } from '@/lib/api-client';
import { assertAnswered, runGuarded, type ActionContext } from './prima-nota-actions';
import { slotKey } from './prima-nota-archive';
import { isSweepResult, type PeriodSource, type StatementFormat } from './prima-nota-model';

type FiscalExportAnswer = { outcome?: string; replaced?: string[] } | undefined;

/** The browser could not read the file: nothing was sent, so nothing can have changed. */
function reportUnreadable(ctx: ActionContext, error: unknown) {
  ctx.setMessage({
    kind: 'error',
    text: ctx.t('primaNota.supply.unreadable', { reason: (error as Error).message }),
  });
}

function toBinaryString(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return binary;
}

export type SourceTarget = {
  kind: PeriodSource['kind'];
  accountKey: string | null;
  format: StatementFormat | null;
};

/**
 * Supply one file. A statement names its account, and — from a slot of one format — that format:
 * the server checks it against the bytes and refuses a PDF dropped into the CSV's slot, so the
 * mistake is said now rather than when the register is generated.
 */
export async function uploadSource(ctx: ActionContext, file: File, target: SourceTarget) {
  const { kind, accountKey, format } = target;
  const { t, period } = ctx;
  await runGuarded(
    ctx,
    slotKey(kind, accountKey, format),
    (reason) => t('primaNota.supply.failed', { reason }),
    async () => {
      let buffer: ArrayBuffer;
      try {
        buffer = await file.arrayBuffer();
      } catch (error) {
        // The browser, not the server: nothing was sent, so nothing can have changed.
        reportUnreadable(ctx, error);
        return;
      }
      const result = await apiClient.POST('/v1/finance/prima-nota/{period}/sources', {
        params: { path: { period } },
        body: {
          kind,
          accountKey,
          ...(format === null ? {} : { format }),
          filename: file.name,
          contentBase64: btoa(toBinaryString(buffer)),
        } as never,
      });
      assertAnswered(result);
      const outcome = (result.data as { outcome?: string } | undefined)?.outcome;
      ctx.setMessage({
        kind: 'ok',
        text:
          outcome === 'already-held'
            ? t('primaNota.supply.alreadyHeld')
            : t('primaNota.supply.stored'),
      });
      ctx.setProduced(null);
      ctx.reload();
    },
  );
}

/** The profile file as a JSON object, or the message that says why it is not one. */
async function readProfileDocument(
  ctx: ActionContext,
  file: File,
): Promise<Record<string, unknown> | null> {
  let text: string;
  try {
    text = await file.text();
  } catch (error) {
    // The browser, not the file's content: said so, or the operator looks for a JSON mistake.
    reportUnreadable(ctx, error);
    return null;
  }
  let document: unknown;
  try {
    document = JSON.parse(text);
  } catch {
    // Distinguished from a rejected upload on purpose: a malformed file is something the
    // operator can fix, and "the server said no" would send them looking in the wrong place.
    ctx.setMessage({ kind: 'error', text: ctx.t('primaNota.profile.notJson') });
    return null;
  }
  if (document === null || typeof document !== 'object' || Array.isArray(document)) {
    ctx.setMessage({ kind: 'error', text: ctx.t('primaNota.profile.notAnObject') });
    return null;
  }
  return document as Record<string, unknown>;
}

/**
 * Load the organization's derivation profile.
 *
 * WHY THIS IS ON THE SCREEN AT ALL. The assessment has always reported `profile-absent` as a
 * BLOCKING gap — no accounts, no dialects, no roles means neither the documents nor the
 * statements can be read — and the screen rendered that sentence and offered nothing. There was
 * no remedy anywhere: no migration seeds a profile, and the only way to create one was a
 * hand-built POST carrying a bearer token. A screen that states a blocking condition it cannot
 * resolve sends the operator to find someone with a terminal.
 *
 * The document is a JSON file because that is the form the profile already has — it is the
 * durable half of the run profile, and an operator receives it rather than composes it. The
 * server strips any period judgement that rides along, which is the expected mistake and not a
 * rare one: the two halves lived in one document until they were split.
 */
export async function uploadProfile(
  ctx: ActionContext,
  file: File,
  profileNote: string,
  onStored: () => void,
) {
  const { t } = ctx;
  await runGuarded(
    ctx,
    'profile',
    (reason) => t('primaNota.profile.failed', { reason }),
    async () => {
      const document = await readProfileDocument(ctx, file);
      if (document === null) {
        return;
      }
      const result = await apiClient.POST('/v1/finance/prima-nota/profile/revisions', {
        body: {
          document,
          note: profileNote.trim().length > 0 ? profileNote.trim() : file.name,
        } as never,
      });
      assertAnswered(result);
      const revision = (result.data as { revision?: number } | undefined)?.revision;
      ctx.setMessage({
        kind: 'ok',
        text: t('primaNota.profile.stored', { revision: revision ?? '?' }),
      });
      onStored();
      ctx.setProduced(null);
      ctx.reload();
    },
  );
}

export async function withdrawSource(
  ctx: ActionContext,
  sourceId: string,
  reason: string,
  onWithdrawn: () => void,
) {
  const { t, period } = ctx;
  await runGuarded(
    ctx,
    sourceId,
    (why) => t('primaNota.supply.withdrawFailed', { reason: why }),
    async () => {
      const result = await apiClient.POST(
        '/v1/finance/prima-nota/{period}/sources/{sourceId}/withdrawal',
        {
          params: { path: { period, sourceId } },
          body: { reason } as never,
        },
      );
      assertAnswered(result);
      onWithdrawn();
      ctx.setProduced(null);
      ctx.reload();
    },
  );
}

/** The message after the documents file was asked of the fiscal export. */
function fiscalExportMessage(ctx: ActionContext, got: FiscalExportAnswer): string {
  if (got?.outcome === 'already-held') {
    return ctx.t('primaNota.fiscalExport.alreadyHeld');
  }
  return (got?.replaced?.length ?? 0) > 0
    ? ctx.t('primaNota.refresh.nexpenseReplaced')
    : ctx.t('primaNota.fiscalExport.stored');
}

/**
 * Ask the fiscal export for the period's documents file, instead of uploading it.
 *
 * The server fetches, checks the sheets and columns the register reads, and only then keeps the
 * file; the screen asks and re-reads the period.
 */
export async function fetchDocuments(ctx: ActionContext) {
  const { t, period } = ctx;
  // nexpense not answering is a 422 with its reason; a 5xx may come after the file was kept.
  await runGuarded(
    ctx,
    'fiscal-export',
    (reason) => t('primaNota.fiscalExport.failed', { reason }),
    async () => {
      const result = await apiClient.POST('/v1/finance/prima-nota/{period}/fiscal-export-imports', {
        params: { path: { period } },
      });
      assertAnswered(result);
      const got = result.data as FiscalExportAnswer;
      ctx.setMessage({ kind: 'ok', text: fiscalExportMessage(ctx, got) });
      ctx.setProduced(null);
      ctx.reload();
    },
  );
}

const SWEEP_PROBLEM_OUTCOMES = ['ambiguous', 'unreadable', 'blocked-by-existing', 'unreachable'];

/**
 * Look for the period's statements on Drive now, instead of waiting for the morning run.
 *
 * The server does the looking, reading and checking; the screen only asks and then re-reads the
 * period, because what changed is the set of files and the archive's word on each account.
 */
export async function sweepArchive(ctx: ActionContext) {
  const { t, period } = ctx;
  // A sweep that failed part-way may still have recorded what it found, and imported files
  // for the accounts that went through.
  await runGuarded(
    ctx,
    'sweep',
    (reason) => t('primaNota.archive.failed', { reason }),
    async () => {
      const result = await apiClient.POST('/v1/finance/prima-nota/{period}/archive-sweeps', {
        params: { path: { period } },
      });
      assertAnswered(result);
      const body: unknown = result.data;
      // "Completed" only when no account ended in a problem: a search that stopped on one account
      // must not be announced in green beside the line that says it stopped.
      const troubled = isSweepResult(body)
        ? body.accounts.filter((row) => SWEEP_PROBLEM_OUTCOMES.includes(row.outcome)).length
        : 0;
      ctx.setMessage(sweepMessage(ctx, body, troubled));
      ctx.setProduced(null);
      ctx.reload();
    },
  );
}

function sweepMessage(ctx: ActionContext, body: unknown, troubled: number) {
  if (troubled > 0) {
    return {
      kind: 'error' as const,
      text: ctx.t('primaNota.archive.sweptWithProblems', { count: troubled }),
    };
  }
  return {
    kind: 'ok' as const,
    text:
      isSweepResult(body) && !body.archiveDeclared
        ? ctx.t('primaNota.archive.undeclared')
        : ctx.t('primaNota.archive.swept'),
  };
}
